import { RefObject, useCallback, useEffect, useRef } from 'react';
import { useKeyboardShortcut } from './useKeyboardShortcut';
import { isPrintableTriggerKey } from '../../utils/shortcuts/isPrintableTriggerKey';
import { isEditableElement } from '../../utils/shortcuts/isEditableElement';

type UseMenuSearchShortcutArgs = {
  // DOM root the shortcut dispatcher uses to decide the side menu owns the keydown.
  scopeRef: RefObject<HTMLElement | null>;
  // The side-menu search input; focused (and selected on Space) when typing starts outside it.
  inputRef: RefObject<HTMLInputElement | null>;
  shortcutId: string;
  disabled?: boolean;
  // When true, plain typing claims the search even with focus/cursor outside the menu (editable
  // elements still win). Used on the dashboard so the user can filter the menu without focusing it.
  activateAnywhere?: boolean;
};

// Owns the side-menu search keyboard interactions, mirroring the grid quick-filter shortcut:
// - typing outside the input focuses it (Space focuses + selects the query instead of being typed),
// - Escape drops focus from the input.
// While the input has focus, printable keys are left alone so editing (incl. a space) behaves normally.
export const useMenuSearchShortcut = ({
  scopeRef,
  inputRef,
  shortcutId,
  disabled,
  activateAnywhere,
}: UseMenuSearchShortcutArgs) => {
  // Whether the mouse cursor currently sits over the side menu.
  const isPointerInsideRef = useRef(false);

  useEffect(() => {
    const scope = scopeRef.current;
    if (!scope) return;

    const onEnter = () => {
      isPointerInsideRef.current = true;
    };
    // Only tracks the cursor; a focused input keeps focus until the user clicks/tabs elsewhere.
    const onLeave = () => {
      isPointerInsideRef.current = false;
    };

    scope.addEventListener('mouseenter', onEnter);
    scope.addEventListener('mouseleave', onLeave);
    return () => {
      scope.removeEventListener('mouseenter', onEnter);
      scope.removeEventListener('mouseleave', onLeave);
    };
  }, [scopeRef]);

  const shouldHandle = useCallback(
    (event: KeyboardEvent) => {
      if (disabled) return false;

      const scope = scopeRef.current;
      const activeElement = document.activeElement;
      const isFocused = activeElement === inputRef.current;
      // Escape is handled only to blur the input, so only when it currently has focus.
      if (event.key === 'Escape') return isFocused;
      // Once the input has focus, let it handle its own typing.
      if (isFocused) return false;

      const focusInsideMenu = !!scope && scope.contains(activeElement);
      // Focus wins over hover: if another input/textarea (e.g. a grid quick filter) has focus, keep
      // typing into it even while the cursor hovers the menu.
      if (!focusInsideMenu && isEditableElement(activeElement)) return false;

      // Only claim the keypress when the user is actually inside the side menu — focus within it or
      // the cursor hovering it. Without this the dispatcher's no-scope fallback would steal keystrokes
      // typed into unrelated inputs (e.g. a form field).
      const isInsideMenu = focusInsideMenu || isPointerInsideRef.current;
      // On the dashboard, plain typing opens the search even from outside the menu; elsewhere the
      // user must be inside it (focus within or cursor hovering) before a keypress is claimed.
      if (!isInsideMenu && !activateAnywhere) return false;

      return isPrintableTriggerKey(event);
    },
    [activateAnywhere, disabled, inputRef, scopeRef]
  );

  const onTrigger = useCallback(
    (event: KeyboardEvent) => {
      const input = inputRef.current;
      if (!input) return;

      if (event.key === 'Escape') {
        input.blur();
        return;
      }

      input.focus();
      // Space must not type into the input; it selects the current query so the next key replaces it.
      if (event.key === ' ') {
        event.preventDefault();
        input.select();
      }
    },
    [inputRef]
  );

  useKeyboardShortcut({
    id: shortcutId,
    scopeRef,
    shouldHandle,
    onTrigger,
  });
};
